import React, { useState } from "react";
import {
  Plus,
  Save,
  Trash2,
  Globe,
  Menu,
  Layout,
  ChevronDown,
  ChevronRight,
  Link2,
} from "lucide-react";

const AdminPanel = () => {
  // 1. Initial State
  const [navItems, setNavItems] = useState([
    { id: 1, label: "Home", path: "/", active: true, children: [] },
    {
      id: 2,
      label: "Products",
      path: "/security-product",
      active: true,
      children: [
        {
          id: 4,
          label: "Software",
          path: "/software",
          active: true,

          children: [
            {
              id: 6,
              label: "Antivirus",
              path: "/av",
              active: true,
              children: [],
            },
          ],
        },
      ],
    },
    { id: 3, label: "Contact", path: "/contact", active: true, children: [] },
  ]);

  const [newItem, setNewItem] = useState({
    label: "",
    path: "",
    parentId: "root",
  });

  // --- LOGIC HELPERS ---

  const addToTree = (list, parentId, itemToAdd) => {
    if (parentId === "root") return [...list, itemToAdd];
    return list.map((node) => {
      if (node.id === parseInt(parentId)) {
        return { ...node, children: [...node.children, itemToAdd] };
      }
      if (node.children.length > 0) {
        return {
          ...node,
          children: addToTree(node.children, parentId, itemToAdd),
        };
      }
      return node;
    });
  };

  const removeFromTree = (list, id) => {
    return list
      .filter((node) => node.id !== id)
      .map((node) => ({
        ...node,
        children: removeFromTree(node.children, id),
      }));
  };

  const handleAddItem = () => {
    if (!newItem.label) return;
    const createdItem = {
      id: Date.now(),
      label: newItem.label,
      path: newItem.path || "#",
      active: true,
      children: [],
    };
    setNavItems(addToTree(navItems, newItem.parentId, createdItem));
    setNewItem({ label: "", path: "", parentId: "root" });
  };

  const getFlatList = (list, depth = 0) => {
    let flat = [];
    list.forEach((item) => {
      flat.push({ id: item.id, label: `${"-".repeat(depth)} ${item.label}` });
      if (item.children)
        flat = [...flat, ...getFlatList(item.children, depth + 1)];
    });
    return flat;
  };

  // --- RECURSIVE COMPONENTS ---

  const ManagementItem = ({ item, depth = 0 }) => (
    <div className="flex flex-col">
      <div
        className={`p-3 flex items-center justify-between hover:bg-slate-800/40 transition-all border-l-2 ${depth > 0 ? "border-blue-500/30 ml-6" : "border-transparent"}`}
      >
        <div className="flex items-center gap-3">
          {depth > 0 ? (
            <Link2 size={14} className="text-slate-600" />
          ) : (
            <Menu size={16} className="text-blue-500" />
          )}
          <div>
            <span className="text-sm font-medium">{item.label}</span>
            <span className="text-[10px] text-slate-500 ml-2 italic">
              {item.path}
            </span>
          </div>
        </div>
        <button
          onClick={() => setNavItems(removeFromTree(navItems, item.id))}
          className="text-slate-500 hover:text-red-400 p-1"
        >
          <Trash2 size={14} />
        </button>
      </div>
      {item.children?.map((child) => (
        <ManagementItem key={child.id} item={child} depth={depth + 1} />
      ))}
    </div>
  );

  // RECURSIVE PREVIEW: This handles infinite dropdown levels
  const PreviewItem = ({ item, isSubMenu = false }) => {
    const hasChildren = item.children && item.children.length > 0;

    return (
      <div className={`group relative ${isSubMenu ? "w-full" : "py-2"}`}>
        <button
          className={`flex items-center justify-between gap-2 text-[11px] font-bold transition-all uppercase tracking-tight 
          ${
            isSubMenu
              ? "px-4 py-2.5 text-slate-400 hover:bg-blue-600 hover:text-white w-full text-left"
              : "text-slate-300 group-hover:text-blue-400"
          }`}
        >
          {item.label}
          {hasChildren &&
            (isSubMenu ? (
              <ChevronRight size={12} />
            ) : (
              <ChevronDown size={12} />
            ))}
        </button>

        {hasChildren && (
          <div
            className={`absolute hidden group-hover:block min-w-[160px] bg-slate-900 border border-slate-700 rounded-lg shadow-2xl py-2 z-50 animate-in fade-in slide-in-from-top-1
            ${isSubMenu ? "left-full top-0 ml-0.5" : "top-full left-0"}`}
          >
            {item.children.map((child) => (
              <PreviewItem key={child.id} item={child} isSubMenu={true} />
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950 p-6 text-white font-sans">
      <div className="max-w-6xl mx-auto space-y-8">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600/20 rounded-lg">
              <Layout className="text-blue-500" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">
              NavMaster <span className="text-blue-500">Customization</span>
            </h1>
          </div>
          <button className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center gap-2 font-bold transition-all shadow-lg shadow-blue-900/20">
            <Save size={18} /> Save Changes
          </button>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            {/* ADD ITEM SECTION */}
            <div className="bg-slate-900 border border-slate-800 p-6 rounded-2xl shadow-xl border-t-4 border-t-blue-600">
              <h2 className="text-lg font-bold mb-6 flex items-center gap-2">
                <Plus size={20} className="text-blue-500" /> Create Link
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <input
                  placeholder="Label"
                  className="bg-slate-800 border-none p-3 rounded-xl text-sm focus:ring-2 ring-blue-500 transition-all outline-none"
                  value={newItem.label}
                  onChange={(e) =>
                    setNewItem({ ...newItem, label: e.target.value })
                  }
                />
                <input
                  placeholder="Path"
                  className="bg-slate-800 border-none p-3 rounded-xl text-sm focus:ring-2 ring-blue-500 transition-all outline-none"
                  value={newItem.path}
                  onChange={(e) =>
                    setNewItem({ ...newItem, path: e.target.value })
                  }
                />
                <select
                  className="bg-slate-800 border-none p-3 rounded-xl text-sm outline-none focus:ring-2 ring-blue-500"
                  value={newItem.parentId}
                  onChange={(e) =>
                    setNewItem({ ...newItem, parentId: e.target.value })
                  }
                >
                  <option value="root">Top Level (No Parent)</option>
                  {getFlatList(navItems).map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      Inside: {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleAddItem}
                className="w-full bg-slate-800 hover:bg-blue-600 p-3 rounded-xl font-bold transition-all border border-slate-700 hover:border-blue-500"
              >
                Add to Navigation Tree
              </button>
            </div>

            {/* MANAGEMENT SECTION */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl overflow-hidden">
              <div className="p-4 bg-slate-800/50 border-b border-slate-800 font-bold text-sm uppercase tracking-widest text-slate-400">
                Structure
              </div>
              <div className="divide-y divide-slate-800/50">
                {navItems.map((item) => (
                  <ManagementItem key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>

          {/* PREVIEW SECTION */}
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-blue-600 to-blue-800 p-6 rounded-2xl shadow-xl">
              <Globe className="mb-4 opacity-50" size={32} />
              <h3 className="text-xl font-bold mb-2">Live Preview</h3>
              <p className="text-blue-100 text-xs font-medium">
                Test your multi-level dropdowns below.
              </p>
            </div>

            <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl shadow-2xl overflow-visible">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-4 overflow-visible">
                <div className="text-blue-500 font-black tracking-tighter">
                  SURAKSHA
                </div>
                <div className="flex gap-4">
                  {navItems.map((item) => (
                    <PreviewItem key={item.id} item={item} />
                  ))}
                </div>
              </div>
              <div className="h-24 bg-slate-800/30 rounded-lg flex items-center justify-center text-[10px] text-slate-600 font-bold uppercase tracking-[0.2em]">
                Page Content
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
